import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["children", "className", "politeness", "role", "announcementDelay", "clearAnnouncementDelay", "onAnnouncementClear", "visible"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import clsx from "clsx";
import PropTypes from "prop-types";
import React, { useEffect, useRef } from "react";
import { DEFAULT_POLITENESS_BY_ROLE, ROLES } from "./constants";
import { conditionallyDelayAnnouncement } from "./utilities";
/**
 * The `LiveRegion` component abstracts the logic for
 * rendering live region content that consistently announces
 * across our supported assistive technologies (e.g. JAWS,
 * VoiceOver, NVDA).
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";

function LiveRegion(_ref) {
  let {
    children,
    className,
    politeness: politenessProp,
    role,
    announcementDelay,
    clearAnnouncementDelay,
    onAnnouncementClear,
    visible
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const ref = useRef();
  const announcementTimeout = useRef();
  const clearAnnouncementTimeout = useRef();
  const markupContainer = useRef();
  let politeness = politenessProp;
  /**
   * We check `undefined` since it is our default,
   * and we want to honor when the user supplies `null`.
   */

  if (typeof politeness === "undefined") {
    politeness = role ? DEFAULT_POLITENESS_BY_ROLE[role] : "assertive";
  }

  useEffect(() => {
    conditionallyDelayAnnouncement({
      announcementTimeout,
      announcementDelay,
      children,
      ref,
      clearAnnouncementDelay,
      clearAnnouncementTimeout,
      markupContainer,
      onAnnouncementClear
    });
  }, [children, announcementDelay, clearAnnouncementDelay, onAnnouncementClear]);
  return _jsx("div", _objectSpread({
    ref: ref,
    "aria-live": politeness,
    role: role,
    className: clsx({
      "wf-u-visually-hidden": !visible
    }, className)
  }, otherProps));
}

LiveRegion.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The time, in milliseconds, after which the live region
   * content should be announced.
   */
  announcementDelay: PropTypes.number,

  /**
   * The content to announce. Typically this will just be
   * a string, although any React.Element is valid.
   */
  children: PropTypes.node,
  className: PropTypes.string,

  /**
   * The time, in milliseconds, after which the live region
   * content should be cleared.
   */
  clearAnnouncementDelay: PropTypes.number,

  /**
   * Callback function that is executed after clearing the
   * announcement.
   */
  onAnnouncementClear: PropTypes.func,

  /**
   * The value to assign to the `aria-live` attribute. If
   * you supply a value, it will be blindly honored.
   * If you do not supply a value, there are a few defaults,
   * depending on the `role` prop:
   *
   * | `role` | `aria-live` default        |
   * |--------|----------------------------|
   * | `null`              | `"assertive"` |
   * | `ROLES.ALERT`       | `null`        |
   * | `ROLES.ALERTDIALOG` | `null`        |
   * | `ROLES.LOG`         | `"polite"`    |
   * | `ROLES.MARQUEE`     | `null`        |
   * | `ROLES.PROGRESSBAR` | `null`        |
   * | `ROLES.STATUS`      | `"polite"`    |
   * | `ROLES.TIMER`       | `"assertive"` |
   *
   * > Note that although the W3C specification may define
   * a default for the `aria-live` attribute for a
   * given role, we may or may not honor it depending on
   * BOSD compatibility. For example, according to W3C,
   * the `timer` role's politeness is "off" by default.
   * However, we set set it to "assertive" in order to
   * ensure announcements.
   */
  politeness: PropTypes.oneOf(["assertive", "polite", "off"]),

  /**
   * In well-known, predefined cases it is better to use a
   * specific "live region role". A default
   * `aria-live` value accompanies each role (see the
   * `politeness` prop documentation), although,
   * at your own risk, you may override it.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Live_Regions#Preferring_specialized_live_region_roles
   */
  role: PropTypes.oneOf([ROLES.ALERT, ROLES.ALERTDIALOG, ROLES.LOG, ROLES.MARQUEE, ROLES.PROGRESSBAR, ROLES.STATUS, ROLES.TIMER]),

  /**
   * Set to `true` to visually display the content, or
   * `false` to render the content into a hidden container.
   */
  visible: PropTypes.bool
} : {};
LiveRegion.defaultProps = {
  children: null,
  className: null,
  // eslint-disable-next-line no-undefined
  politeness: undefined,
  role: null,
  announcementDelay: null,
  clearAnnouncementDelay: null,
  onAnnouncementClear: null,
  visible: false
};
export default LiveRegion;